/* eslint-disable camelcase */
import { useState } from 'react'
import * as FiIcons from 'react-icons/fi'
import { formatCurrency, convertCurrency } from '../Library/Tools/CurrencyConverter'
import {
  ItemRow,
  ItemInfo,
  ItemMain,
  ItemTitle,
  ItemDescription,
  ItemPrice,
  ItemActions,
  QuantityGroup,
  QuantityLabel,
  QuantityInput,
  ButtonGroup,
  InfoButton,
  AddButton
} from './ArticoliDrawer.styles'

export default function ArticoloRow ({ item, tipo, isAdded, onAdd, onInfo, valuta }) {
  const [qta, setQta] = useState(1)

  // Valuta azienda - deve essere dichiarata prima della funzione getPrezzo()
  const valutaAzienda = valuta || 'EUR'

  const handleAdd = () => {
    const qtaNum = parseInt(qta) || 0
    if (qtaNum > 0) {
      onAdd(item, qtaNum)
    }
  }

  const handleKeyPress = (e) => {
    const qtaNum = parseInt(qta) || 0
    if (e.key === 'Enter' && qtaNum > 0) {
      handleAdd()
    }
  }

  const handleQtaChange = (e) => {
    const value = e.target.value
    // Permetti stringa vuota temporaneamente
    if (value === '') {
      setQta('')
    } else {
      const num = parseInt(value)
      if (!isNaN(num) && num >= 0) {
        setQta(num)
      }
    }
  }

  /**
   * Calcola il prezzo da mostrare in base al tipo di item
   *
   * IMPORTANTE: Questa formula deve SEMPRE corrispondere a PricingEngine.calculateBaseCost()
   * nel backend (ems-be/src/services/PricingEngine.js)
   *
   * Formula per articoli:
   * 1. Assicurazione = ((prezzoAcquisto * cap + trasporto) * 1.10 * 0.002) / cap
   * 2. costoBase = prezzoAcquisto + (trasporto/cap) + (container/cap) + (trasporto2/cap) + assicurazione
   * 3. costoAlPezzo = costoBase * (1 + dazi/100)
   *
   * @param {boolean} convertToCompanyCurrency - Se true, converte in valutaAzienda, altrimenti valuta originale
   * @returns {number} Prezzo calcolato
   */
  const getPrezzo = (convertToCompanyCurrency = true) => {
    if (tipo === 'articoli') {
      // Per articoli: calcola il prezzo al pezzo dinamicamente
      // NOTA: Questa formula deve corrispondere a backend/PricingEngine.calculateBaseCost()
      const {
        prezzoAcquisto = 0,
        valuta = 'EUR',
        trasporto = 0,
        trasportoValuta = 'EUR',
        costoContainer = 0,
        containerValuta = 'EUR',
        trasporto2 = 0,
        trasporto2Valuta = 'EUR',
        capacita_trasporto = 1,
        dazi = 0
      } = item

      if (capacita_trasporto === 0) {
        return 0
      }

      // STEP 1: Conversioni (se richiesto)
      const prezzoAcquistoConverted = convertToCompanyCurrency
        ? convertCurrency(prezzoAcquisto, valuta, valutaAzienda)
        : prezzoAcquisto
      const trasportoConverted = convertToCompanyCurrency
        ? convertCurrency(trasporto, trasportoValuta, valutaAzienda)
        : convertCurrency(trasporto, trasportoValuta, valuta)
      const containerConverted = convertToCompanyCurrency
        ? convertCurrency(costoContainer, containerValuta, valutaAzienda)
        : convertCurrency(costoContainer, containerValuta, valuta)
      const trasporto2Converted = convertToCompanyCurrency
        ? convertCurrency(trasporto2, trasporto2Valuta, valutaAzienda)
        : convertCurrency(trasporto2, trasporto2Valuta, valuta)

      // STEP 2: Calcolo assicurazione (su container intero)
      const valoreMerceTotale = prezzoAcquistoConverted * capacita_trasporto
      const assicurazioneTotale = (valoreMerceTotale + trasportoConverted) * 1.10 * 0.002
      const assicurazionePerPezzo = assicurazioneTotale / capacita_trasporto

      // STEP 3: Calcola costo base per pezzo (con tutti i 5 componenti)
      const costoBase = prezzoAcquistoConverted
        + (trasportoConverted / capacita_trasporto)
        + (containerConverted / capacita_trasporto)
        + (trasporto2Converted / capacita_trasporto)
        + assicurazionePerPezzo

      // STEP 4: Applica dazi (customs duty)
      const costoAlPezzo = costoBase * (1 + dazi / 100)

      return costoAlPezzo
    } else {
      // Per servizi: usa il costo direttamente (no formula complessa)
      return item.costo || 0
    }
  }

  // Calcola prezzo in entrambe le valute
  const valutaItem = item.valuta || 'EUR'
  const isDifferentCurrency = valutaItem !== valutaAzienda

  // Prezzo nella valuta originale dell'articolo (senza conversione a valutaAzienda)
  const prezzoCalcolato = getPrezzo(false)

  // Prezzo convertito in valutaAzienda (per mostrare come prezzo principale)
  const prezzo = isDifferentCurrency ? getPrezzo(true) : prezzoCalcolato

  const qtaNum = parseInt(qta) || 0
  const subtotale = prezzo * qtaNum

  // Info secondarie da mostrare
  const getSecondaryInfo = () => {
    if (tipo === 'articoli') {
      const parts = []
      if (item.referenza) parts.push(`Ref: ${item.referenza}`)
      if (item.fornitore?.fornitore) parts.push(item.fornitore.fornitore)
      if (item.produttore?.produttore) parts.push(item.produttore.produttore)
      return parts.join(' • ')
    } else {
      return item.descrizione || ''
    }
  }

  return (
    <ItemRow isAdded={isAdded}>
      <ItemInfo>
        <ItemMain>
          <ItemTitle>{item.titolo || 'Senza titolo'}</ItemTitle>
          <ItemDescription>{getSecondaryInfo()}</ItemDescription>
        </ItemMain>
        <ItemPrice>
          {formatCurrency(prezzo, valutaAzienda)}
          {isDifferentCurrency && (
            <span style={{ fontSize: '0.85em', color: '#6b7280', marginLeft: '4px' }}>
              ({formatCurrency(prezzoCalcolato, valutaItem)})
            </span>
          )}
        </ItemPrice>
      </ItemInfo>

      <ItemActions>
        <QuantityGroup>
          <QuantityLabel>Qta:</QuantityLabel>
          <QuantityInput
            type='number'
            min='0'
            step='1'
            value={qta}
            onChange={handleQtaChange}
            onKeyPress={handleKeyPress}
            disabled={isAdded}
            placeholder='Qta'
          />
          {qtaNum > 1 && (
            <ItemDescription>
              = {formatCurrency(subtotale, valutaAzienda)}
              {isDifferentCurrency && (
                <span style={{ fontSize: '0.9em', color: '#6b7280', marginLeft: '4px' }}>
                  ({formatCurrency(prezzoCalcolato * qtaNum, valutaItem)})
                </span>
              )}
            </ItemDescription>
          )}
        </QuantityGroup>

        <ButtonGroup>
          {tipo === 'articoli' && onInfo && (
            <InfoButton
              onClick={() => onInfo(item)}
              title='Visualizza dettagli'
              type='button'
            >
              <FiIcons.FiInfo size={18} />
            </InfoButton>
          )}

          <AddButton
            isAdded={isAdded}
            onClick={handleAdd}
            disabled={isAdded || qtaNum <= 0}
            type='button'
          >
            {isAdded
              ? (
                <>
                  <FiIcons.FiCheckCircle size={16} />
                  Aggiunto
                </>
                )
              : (
                <>
                  <FiIcons.FiPlus size={16} />
                  Aggiungi
                </>
                )}
          </AddButton>
        </ButtonGroup>
      </ItemActions>
    </ItemRow>
  )
}
